import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import financeService from '../services/financeService';
import {
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell
} from 'recharts';

const INITIAL_FILTERS = {
  fromDate: '',
  toDate: '',
  year: '',
  country: '',
  jobRole: '',
  applicant: '',
  paymentType: ''
};

const INITIAL_REFUND_STATE = {
  open: false,
  transaction: null,
  amount: '',
  reason: '',
  refundTransactionNumber: ''
};

const INITIAL_APPROVE_STATE = {
  open: false,
  request: null,
  refundTransactionNumber: '',
  refundAmount: ''
};

const currency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(amount);
};

const StatCard = ({ title, value }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
    <p className="text-xs uppercase tracking-wide text-gray-500">{title}</p>
    <p className="text-2xl mt-2 font-semibold text-charcoal">{currency(value)}</p>
  </div>
);

const ChairmanFinancePage = () => {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [dashboard, setDashboard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [refundRequests, setRefundRequests] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [reviewLoadingKey, setReviewLoadingKey] = useState('');
  const [refundState, setRefundState] = useState(INITIAL_REFUND_STATE);
  const [approveState, setApproveState] = useState(INITIAL_APPROVE_STATE);
  const [exporting, setExporting] = useState(false);

  const formatApplicationDisplayId = (transaction) => {
    // Only format Application ID for application-related transactions
    if (!['agreement', 'payment', 'corporate'].includes(transaction?.sourceType)) {
      return '-';
    }

    const rawId = transaction?.sourceRefId ? transaction.sourceRefId.toString() : '';
    if (!rawId) return 'N/A';

    const country = (transaction?.country || '').trim();
    const countryPrefix = country
      ? country.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase().padEnd(2, 'X')
      : 'XX';

    const stableSuffix = rawId.slice(-6).toUpperCase();
    return `${countryPrefix}${stableSuffix}`;
  };

  const summary = dashboard?.summary || {
    totalRevenue: 0,
    applicantPayments: 0,
    corporatePayments: 0,
    refunds: 0,
    hrExpense: 0,
    officeExpense: 0,
    netRevenue: 0
  };

  const financialBreakdownData = useMemo(() => ([
    { name: 'Applicant', value: Number(summary.applicantPayments || 0) },
    { name: 'Corporate', value: Number(summary.corporatePayments || 0) },
    { name: 'Refunds', value: Number(summary.refunds || 0) },
    { name: 'HR Expense', value: Number(summary.hrExpense || 0) },
    { name: 'Office Expense', value: Number(summary.officeExpense || 0) }
  ]), [summary]);

  const cashflowData = useMemo(() => {
    const bucket = {};

    transactions.forEach((txn) => {
      if (!txn.transactionDate) {
        return;
      }

      const dateKey = new Date(txn.transactionDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short'
      });

      if (!bucket[dateKey]) {
        bucket[dateKey] = { date: dateKey, credit: 0, debit: 0 };
      }

      const amount = Number(txn.amount || 0);
      if (txn.creditDebit === 'CREDIT') {
        bucket[dateKey].credit += amount;
      } else {
        bucket[dateKey].debit += amount;
      }
    });

    return Object.values(bucket)
      .slice(-8)
      .map((item) => ({
        ...item,
        credit: Math.round(item.credit * 100) / 100,
        debit: Math.round(item.debit * 100) / 100
      }));
  }, [transactions]);

  const breakdownColors = ['#3B82F6', '#22C55E', '#EF4444', '#F59E0B', '#8B5CF6'];

  const fetchFinanceData = async (page = pagination.page, limit = pagination.limit) => {
    try {
      setLoading(true);
      const [dashboardResponse, transactionsResponse, refundRequestsResponse] = await Promise.all([
        financeService.getDashboard(filters),
        financeService.getTransactions(filters, page, limit),
        financeService.getRefundRequests({ ...filters, status: 'pending' })
      ]);

      if (!dashboardResponse.success || !transactionsResponse.success || !refundRequestsResponse?.success) {
        toast.error('Failed to load finance data');
        return;
      }

      console.log('Finance transactions sample:', transactionsResponse.data?.[0]); // Debug log

      setDashboard(dashboardResponse.data);
      setTransactions(transactionsResponse.data || []);
      setRefundRequests(refundRequestsResponse.data || []);
      setPagination(transactionsResponse.pagination || { page: 1, totalPages: 1, total: 0, limit: 20 });
    } catch (error) {
      console.error('Finance fetch error:', error);
      toast.error(error?.response?.data?.error || 'Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinanceData(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const serialStart = useMemo(() => ((pagination.page || 1) - 1) * (pagination.limit || 20), [pagination]);

  const onFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters(INITIAL_FILTERS);
  };

  const onPageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > (pagination.totalPages || 1)) {
      return;
    }
    fetchFinanceData(nextPage, pagination.limit);
  };

  const openRefundModal = (transaction) => {
    setRefundState({
      open: true,
      transaction,
      amount: '',
      reason: ''
    });
  };

  const closeRefundModal = () => {
    setRefundState(INITIAL_REFUND_STATE);
  };

  const submitRefund = async (event) => {
    event.preventDefault();

    if (!refundState.transaction) {
      return;
    }

    try {
      const payload = {
        sourceType: refundState.transaction.sourceType,
        sourceRefId: refundState.transaction.sourceRefId,
        paymentType: refundState.transaction.paymentType,
        amount: Number(refundState.amount),
        reason: refundState.reason,
        refundTransactionNumber: refundState.refundTransactionNumber,
        dateTime: refundState.transaction.dateTime,
        transactionDate: refundState.transaction.transactionDate,
        country: refundState.transaction.country,
        jobApplied: refundState.transaction.jobApplied,
        applicantName: refundState.transaction.applicantName,
        applicantIdentifier: refundState.transaction.applicantIdentifier,
        transactionId: refundState.transaction.transactionId,
        remarks: refundState.transaction.remarks
      };

      const response = await financeService.refundTransaction(payload);
      if (!response.success) {
        toast.error('Refund failed');
        return;
      }

      toast.success('Refund recorded successfully');
      closeRefundModal();
      fetchFinanceData(pagination.page, pagination.limit);
    } catch (error) {
      console.error('Refund error:', error);
      toast.error(error?.response?.data?.error || 'Refund failed');
    }
  };

  const exportPdf = async () => {
    try {
      setExporting(true);
      await financeService.exportPdf(filters);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const exportExcel = async () => {
    try {
      setExporting(true);
      await financeService.exportExcel(filters);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Excel export failed');
    } finally {
      setExporting(false);
    }
  };

  const reviewRefundRequest = async (request, action) => {
    if (action === 'reject') {
      const reason = window.prompt('Enter rejection reason (minimum 5 characters):', '');
      if (!reason || reason.trim().length < 5) {
        toast.error('Rejection reason is required');
        return;
      }

      const loadingKey = `${request.applicationId}-${request.requestType}-${action}`;
      try {
        setReviewLoadingKey(loadingKey);
        const response = await financeService.reviewRefundRequest(request.applicationId, {
          requestType: request.requestType,
          action,
          reason: reason.trim(),
          refundTransactionNumber: ''
        });

        if (!response.success) {
          toast.error('Failed to review refund request');
          return;
        }

        toast.success('Refund request rejected successfully');
        fetchFinanceData(pagination.page, pagination.limit);
      } catch (error) {
        toast.error(error?.response?.data?.error || 'Failed to review refund request');
      } finally {
        setReviewLoadingKey('');
      }
    } else if (action === 'approve') {
      // Open approve modal
      setApproveState({
        open: true,
        request,
        refundTransactionNumber: '',
        refundAmount: request.amount?.toString() || ''
      });
    }
  };

  const closeApproveModal = () => {
    setApproveState(INITIAL_APPROVE_STATE);
  };

  const submitApproveRefund = async (event) => {
    event.preventDefault();

    if (!approveState.request) {
      return;
    }

    if (!approveState.refundTransactionNumber || approveState.refundTransactionNumber.trim().length < 1) {
      toast.error('Refund transaction number is required');
      return;
    }

    if (!approveState.refundAmount || Number(approveState.refundAmount) <= 0) {
      toast.error('Refund amount must be greater than 0');
      return;
    }

    const loadingKey = `${approveState.request.applicationId}-${approveState.request.requestType}-approve`;
    try {
      setReviewLoadingKey(loadingKey);
      const response = await financeService.reviewRefundRequest(approveState.request.applicationId, {
        requestType: approveState.request.requestType,
        action: 'approve',
        reason: '',
        refundTransactionNumber: approveState.refundTransactionNumber.trim(),
        refundAmount: Number(approveState.refundAmount)
      });

      if (!response.success) {
        toast.error('Failed to approve refund request');
        return;
      }

      toast.success('Refund request approved successfully');
      closeApproveModal();
      fetchFinanceData(pagination.page, pagination.limit);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to approve refund request');
    } finally {
      setReviewLoadingKey('');
    }
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-4 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-charcoal">Finance Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">Revenue, expenses, refunds, and transaction insights.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportPdf}
              disabled={exporting}
              className="px-4 py-2 bg-charcoal text-white rounded-lg text-sm disabled:opacity-60"
            >
              Export PDF
            </button>
            <button
              onClick={exportExcel}
              disabled={exporting}
              className="px-4 py-2 bg-olive text-white rounded-lg text-sm disabled:opacity-60"
            >
              Export Excel
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <input type="date" name="fromDate" value={filters.fromDate} onChange={onFilterChange} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <input type="date" name="toDate" value={filters.toDate} onChange={onFilterChange} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <input type="number" name="year" value={filters.year} onChange={onFilterChange} placeholder="Year" className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <input type="text" name="country" value={filters.country} onChange={onFilterChange} placeholder="Country" className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <input type="text" name="jobRole" value={filters.jobRole} onChange={onFilterChange} placeholder="Job Role" className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <input type="text" name="applicant" value={filters.applicant} onChange={onFilterChange} placeholder="Applicant Name or ID" className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <select name="paymentType" value={filters.paymentType} onChange={onFilterChange} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <option value="">All Payment Types</option>
              <option value="applicant">Applicant</option>
              <option value="corporate">Corporate</option>
            </select>
            <button onClick={clearFilters} className="px-4 py-2 bg-gray-100 text-charcoal rounded-lg text-sm">Clear</button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          <StatCard title="Total Revenue" value={summary.totalRevenue} />
          <StatCard title="Applicant Payments" value={summary.applicantPayments} />
          <StatCard title="Corporate Payments" value={summary.corporatePayments} />
          <StatCard title="Refunds" value={summary.refunds} />
          <StatCard title="HR Expense" value={summary.hrExpense} />
          <StatCard title="Office Expense" value={summary.officeExpense} />
          <StatCard title="Net Revenue" value={summary.netRevenue} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h2 className="text-base font-semibold text-charcoal">Financial Distribution</h2>
            <p className="text-xs text-gray-500 mt-1">Payments, refunds, and expenses split.</p>
            <div className="h-72 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={financialBreakdownData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={95}
                    innerRadius={55}
                    paddingAngle={2}
                  >
                    {financialBreakdownData.map((entry, index) => (
                      <Cell key={`${entry.name}-${index}`} fill={breakdownColors[index % breakdownColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => currency(value)} />
                  <Legend verticalAlign="bottom" height={24} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h2 className="text-base font-semibold text-charcoal">Recent Cashflow Trend</h2>
            <p className="text-xs text-gray-500 mt-1">Credit vs debit across recent transaction dates.</p>
            <div className="h-72 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cashflowData} margin={{ left: 8, right: 8, top: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `₹${Number(value || 0).toLocaleString('en-IN')}`} />
                  <Tooltip formatter={(value) => currency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="credit" name="Credit" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="debit" name="Debit" stroke="#DC2626" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <h2 className="text-base font-semibold text-charcoal">Summary Comparison</h2>
          <p className="text-xs text-gray-500 mt-1">Quick comparison of key financial buckets.</p>
          <div className="h-72 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { metric: 'Applicant', amount: Number(summary.applicantPayments || 0) },
                  { metric: 'Corporate', amount: Number(summary.corporatePayments || 0) },
                  { metric: 'Refunds', amount: Number(summary.refunds || 0) },
                  { metric: 'HR', amount: Number(summary.hrExpense || 0) },
                  { metric: 'Office', amount: Number(summary.officeExpense || 0) },
                  { metric: 'Net', amount: Number(summary.netRevenue || 0) }
                ]}
                margin={{ left: 8, right: 8, top: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="metric" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `₹${Number(value || 0).toLocaleString('en-IN')}`} />
                <Tooltip formatter={(value) => currency(value)} />
                <Bar dataKey="amount" fill="#1F2937" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-charcoal">Pending Candidate Refund Requests</h2>
            <span className="text-sm text-gray-500">{refundRequests.length} pending</span>
          </div>

          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="p-3 text-left">Requested At</th>
                  <th className="p-3 text-left">Applicant</th>
                  <th className="p-3 text-left">Country</th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-left">Amount</th>
                  <th className="p-3 text-left">Reason</th>
                  <th className="p-3 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="p-6 text-center text-gray-500">Loading refund requests...</td></tr>
                ) : refundRequests.length === 0 ? (
                  <tr><td colSpan={7} className="p-6 text-center text-gray-500">No pending refund requests.</td></tr>
                ) : (
                  refundRequests.map((request) => (
                    <tr key={`${request.applicationId}-${request.requestType}-${request.requestedAt || ''}`} className="border-t border-gray-100">
                      <td className="p-3">{request.requestedAt ? new Date(request.requestedAt).toLocaleString() : '-'}</td>
                      <td className="p-3">{request.applicantName} ({request.applicantIdentifier})</td>
                      <td className="p-3">{request.country}</td>
                      <td className="p-3 capitalize">{request.requestType}</td>
                      <td className="p-3 font-semibold">{currency(request.amount)}</td>
                      <td className="p-3">{request.reason}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => reviewRefundRequest(request, 'approve')}
                            disabled={reviewLoadingKey === `${request.applicationId}-${request.requestType}-approve` || reviewLoadingKey === `${request.applicationId}-${request.requestType}-reject`}
                            className="px-3 py-1 bg-green-100 text-green-700 rounded-md text-xs disabled:opacity-60"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => reviewRefundRequest(request, 'reject')}
                            disabled={reviewLoadingKey === `${request.applicationId}-${request.requestType}-approve` || reviewLoadingKey === `${request.applicationId}-${request.requestType}-reject`}
                            className="px-3 py-1 bg-red-100 text-red-700 rounded-md text-xs disabled:opacity-60"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="p-3 text-left">S.No</th>
                  <th className="p-3 text-left">Application ID</th>
                  <th className="p-3 text-left">Date & Time</th>
                  <th className="p-3 text-left">Country</th>
                  <th className="p-3 text-left">Job Applied</th>
                  <th className="p-3 text-left">Candidate Name</th>
                  <th className="p-3 text-left">Credit/Debit</th>
                  <th className="p-3 text-left">Payment Type</th>
                  <th className="p-3 text-left">Transaction ID</th>
                  <th className="p-3 text-left">Amount</th>
                  <th className="p-3 text-left">Transaction Date</th>
                  <th className="p-3 text-left">Remarks</th>
                  <th className="p-3 text-left">Verified By</th>
                  <th className="p-3 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={14} className="p-6 text-center text-gray-500">Loading transactions...</td></tr>
                ) : transactions.length === 0 ? (
                  <tr><td colSpan={14} className="p-6 text-center text-gray-500">No transactions found.</td></tr>
                ) : (
                  transactions.map((txn, index) => (
                    <tr key={`${txn.sourceKey}-${index}`} className="border-t border-gray-100">
                      <td className="p-3">{serialStart + index + 1}</td>
                      <td className="p-3">
                        <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-semibold">
                          {formatApplicationDisplayId(txn)}
                        </span>
                      </td>
                      <td className="p-3">{txn.dateTime ? new Date(txn.dateTime).toLocaleString() : '-'}</td>
                      <td className="p-3">{txn.country}</td>
                      <td className="p-3">{txn.jobApplied}</td>
                      <td className="p-3">{txn.applicantName}</td>
                      <td className={`p-3 ${txn.creditDebit === 'CREDIT' ? 'text-green-600' : 'text-red-600'}`}>{txn.creditDebit}</td>
                      <td className="p-3 capitalize">{txn.paymentType}</td>
                      <td className="p-3">{txn.transactionId}</td>
                      <td className="p-3 font-semibold">{currency(txn.amount)}</td>
                      <td className="p-3">{txn.transactionDate ? new Date(txn.transactionDate).toLocaleDateString() : '-'}</td>
                      <td className="p-3">{txn.remarks}</td>
                      <td className="p-3">{txn.verifiedBy ? (txn.verifiedBy.name || txn.verifiedBy.email || txn.verifiedBy.identifier || txn.verifiedBy) : '-'}</td>
                      <td className="p-3">
                        {(txn.sourceType === 'agreement' || txn.sourceType === 'payment' || txn.sourceType === 'corporate') ? (
                          <button onClick={() => openRefundModal(txn)} className="px-3 py-1 bg-red-100 text-red-700 rounded-md text-xs">Refund</button>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between p-3 border-t border-gray-100">
            <p className="text-sm text-gray-500">Total: {pagination.total || 0} records</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onPageChange((pagination.page || 1) - 1)}
                disabled={(pagination.page || 1) <= 1}
                className="px-3 py-1 rounded-md border border-gray-300 text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm">Page {pagination.page || 1} / {pagination.totalPages || 1}</span>
              <button
                onClick={() => onPageChange((pagination.page || 1) + 1)}
                disabled={(pagination.page || 1) >= (pagination.totalPages || 1)}
                className="px-3 py-1 rounded-md border border-gray-300 text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {refundState.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <form onSubmit={submitRefund} className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-xl font-semibold text-charcoal">Create Refund</h2>
            <p className="text-sm text-gray-600">Transaction: {refundState.transaction?.transactionId}</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Refund Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={refundState.amount}
                onChange={(event) => setRefundState((prev) => ({ ...prev, amount: event.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Refund Transaction Number *</label>
              <input
                type="text"
                value={refundState.refundTransactionNumber}
                onChange={(event) => setRefundState((prev) => ({ ...prev, refundTransactionNumber: event.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Enter refund transaction number"
                required
                maxLength={100}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <textarea
                value={refundState.reason}
                onChange={(event) => setRefundState((prev) => ({ ...prev, reason: event.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[100px]"
                required
                minLength={3}
                maxLength={500}
              />
            </div>

            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={closeRefundModal} className="px-4 py-2 bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg">Submit Refund</button>
            </div>
          </form>
        </div>
      )}

      {approveState.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <form onSubmit={submitApproveRefund} className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-xl font-semibold text-green-600">Approve Refund Request</h2>
            <div className="bg-gray-50 p-3 rounded-lg space-y-1">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Applicant:</span> {approveState.request?.applicantName}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Type:</span> {approveState.request?.requestType}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Requested Amount:</span> {currency(approveState.request?.amount || 0)}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Reason:</span> {approveState.request?.reason}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Refund Amount *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={approveState.refundAmount}
                onChange={(event) => setApproveState((prev) => ({ ...prev, refundAmount: event.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Enter refund amount"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum refundable: {currency(approveState.request?.amount || 0)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Refund Transaction Number *</label>
              <input
                type="text"
                value={approveState.refundTransactionNumber}
                onChange={(event) => setApproveState((prev) => ({ ...prev, refundTransactionNumber: event.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Enter refund transaction number"
                required
                maxLength={100}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the transaction ID from your payment system
              </p>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeApproveModal}
                disabled={reviewLoadingKey !== ''}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={reviewLoadingKey !== ''}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {reviewLoadingKey !== '' ? 'Processing...' : 'Approve Refund'}
              </button>
            </div>
          </form>
        </div>
      )}
    </MainLayout>
  );
};

export default ChairmanFinancePage;
